import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import ReceipeDetail from "../RequisitionProduction/ReceipeDetail";

const Edit = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const { id } = useParams();
  const [error, setError] = useState({});
  const [product, setProduct] = useState([]);
  const [rmpm, setRmpm] = useState([]);
  const [list, setList] = useState([]);
  const { checkRequiredFields } = useRequiredFields();
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setInputs((values) => ({
      ...values,
      product_id: selectedOption ? selectedOption.value : "",
    }));
    setSelectedProductOption(selectedOption);
  };

  const handleCustomInputChange = (e, index) => {
    const { name, value } = e.target;
    const inputInfo = [...list];
    inputInfo[index][name] = value;
    setList(inputInfo);
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const resProduct = await Http.get("/receipe-finished-good");
        setProduct(resProduct.data);

        const resRmpm = await Http.get("/rmpm-with-stock");
        setRmpm(resRmpm.data);

        const resProduction = await Http.get("/damage/" + id);
        console.log("Production data:", resProduction.data);

        // Format date to YYYY-MM-DD for HTML5 date input
        let formattedDate = resProduction.data.date;
        if (formattedDate && formattedDate.includes("/")) {
          // Convert DD/MM/YYYY or similar formats to YYYY-MM-DD
          const dateParts = formattedDate.split("/");
          if (dateParts.length === 3) {
            formattedDate = `${dateParts[2]}-${dateParts[1].padStart(2, "0")}-${dateParts[0].padStart(2, "0")}`;
          }
        }

        setInputs({
          date: formattedDate || resProduction.data.date || "",
          time: resProduction.data.time || "",
          receipt_no: resProduction.data.receipt_no || "",
          batch_size: resProduction.data.batch_size || "",
          product_id: resProduction.data.product_id || "",
          notes: resProduction.data.notes || "",
          batch_costing: resProduction.data.batch_costing || "",
        });

        if (resProduction.data.product_id && resProduction.data.product) {
          setSelectedProductOption({
            label: resProduction.data.product.name,
            value: resProduction.data.product_id,
          });
        }

        const listInfo = [];
        if (resProduction.data.items && resProduction.data.items.length > 0) {
          resProduction.data.items.forEach((item) => {
            listInfo.push({
              id: item.product_id,
              code: item.product ? item.product.code : "",
              name: item.product ? item.product.name : "",
              product_id: item.product_id,
              issue_qty: item.qty,
            });
          });
        }
        setList(listInfo);
        console.log("Set inputs:", {
          date: formattedDate || resProduction.data.date,
          time: resProduction.data.time,
          receipt_no: resProduction.data.receipt_no,
          batch_size: resProduction.data.batch_size,
          product_id: resProduction.data.product_id,
        });
      } catch (error) {
        console.error("Fetch error:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to load production data. Please try again.",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    DatatableFunc();
    fetchData();
  }, [id]);

  const addMoreList = (productId) => {
    setList((prevList) => {
      const exists = prevList.some(
        (item) => parseInt(item.id) === parseInt(productId),
      );

      if (exists) {
        Swal.fire({
          text: "This Product already in the list! Please choose another Product!",
          icon: "warning",
          showCancelButton: false,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Ok",
        });
        return prevList;
      }

      Http.get("/add-new-rmpm-receipe/" + productId).then((res) => {
        setList((current) => [...current, { ...res.data, issue_qty: 1 }]);
      });

      return prevList;
    });
  };

  const removeList = (index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const inputInfo = [...list];
        inputInfo.splice(index, 1);
        setList(inputInfo);
      }
    });
  };

  const formSubmit = () => {
    checkRequiredFields();

    const preparedItems =
      list.length > 0
        ? list.map((item) => ({
            product_id: item.product_id || item.id,
            issue_qty: parseFloat(item.issue_qty) || 0,
            description: item.description || "",
          }))
        : [];

    const submitData = {
      ...inputs,
      items: preparedItems,
    };

    Http.put("/production/" + id, submitData)
      .then((res) => {
        navigate("/production", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch(function (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          setError(e.response.data.errors);
        }
      });
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small">{error[fieldName]}</div>;
    }
    return null;
  };

  const cols = [
    { field: "code", header: "Code" },
    { field: "name", header: "Item" },
    { field: (item) => item.unit_qty + " " + item.unit_name, header: "Unit" },
    { field: "stockQty", header: "Stock" },
  ];

  const actionTemplate = (item, deleteData) => {
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => addMoreList(item.id)}
      >
        <i className="fa fa-plus"></i>
      </button>
    );
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Production</h5>
        </div>
        <div className="am-pagebody row d-flex justify-content-evenly">
          <div className="card pd-20 pd-sm-40 col-7">
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Edit Production
            </h6>
            <hr></hr>
            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>
                      Date <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleChange}
                      placeholder="Input Date"
                      data-required
                    />
                    {displayError("date")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Time <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      name="time"
                      value={inputs.time || ""}
                      onChange={handleChange}
                      placeholder="Input Time"
                      data-required
                    />
                    {displayError("time")}
                  </div>
                  <div className="col-lg">
                    <label>
                      Receipt No. <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="receipt_no"
                      value={inputs.receipt_no || ""}
                      onChange={handleChange}
                      placeholder="Input Receipt No"
                      data-required
                    />
                    {displayError("receipt_no")}
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-lg">
                    <label>
                      Batch Size <i className="fa fa-star required"></i>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="batch_size"
                      value={inputs.batch_size || ""}
                      onChange={handleChange}
                      placeholder="Input Batch Size"
                      data-required
                    />
                    {displayError("batch_size")}
                  </div>
                  <div className="col-lg">
                    <label htmlFor="">
                      Finished Good <i className="fa fa-star required"></i>
                    </label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={selectedProductOption}
                      name="product_id"
                      options={product}
                      onChange={handleSelectChange}
                      data-required
                    />
                    {displayError("product_id")}
                  </div>
                </div>
                {!(inputs.fg_out > 0) && (
                  <div className="row mt-3">
                    <div className="card-solid">
                      <div className="card-header with-border text-center">
                        <h5 className="card-title mb-0">Items List</h5>
                      </div>
                      <div className="card-body mt-3 p-0 pt-3">
                        <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0">
                          <thead>
                            <tr>
                              <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                Code
                              </th>
                              <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                Product
                              </th>
                              <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                Qty
                              </th>
                              <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(list).map((x, i) => (
                              <ReceipeDetail
                                key={i}
                                listInfo={x}
                                listSl={i}
                                rmpm={rmpm}
                                remove={() => removeList(i)}
                                listInputChange={(e) =>
                                  handleCustomInputChange(e, i)
                                }
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-6">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                    >
                      <i className="fa fa-send mg-r-10"></i> Update
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      className="btn btn-secondary btn-block mg-b-10"
                      onClick={() => navigate("/production")}
                    >
                      <i className="fa fa-arrow-left mg-r-10"></i> Back
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <div
            className="card pd-20 pd-sm-40 col-4"
            style={{ backgroundColor: "#ffffffc7" }}
          >
            <h6 className="card-body-title mg-sm-b-30 text-center">
              Finished Good
            </h6>
            <hr></hr>

            <ReactDataTable
              cols={cols}
              receiveData={rmpm}
              isLoading={loading}
              actionTemplate={actionTemplate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edit;
